import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useAuth } from '../context/Authcontext';

const PLAN_META = {
  emi: {
    title: 'Monthly EMI Plan',
    accent: 'from-violet-500 to-indigo-500',
    shadow: 'shadow-indigo-300/60',
  },
  full: {
    title: 'Full Settlement in 1st Month',
    accent: 'from-cyan-500 to-teal-500',
    shadow: 'shadow-cyan-300/60',
  },
};

const SUCCESS_LOTTIE_PATH = `${process.env.PUBLIC_URL || ''}/lottie/success-check.json`;

function PaymentVerificationStatus() {
  const location = useLocation();
  const { user } = useAuth();
  const selectedPlanId = location.state?.selectedPlan || 'emi';
  const plan = PLAN_META[selectedPlanId] || PLAN_META.emi;
  const planTitle = location.state?.planName || plan.title;
  const notifyEmail = (user?.email || '').trim();
  const [animationData, setAnimationData] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadAnimation = async () => {
      try {
        const response = await fetch(SUCCESS_LOTTIE_PATH);
        if (!response.ok) return;
        const data = await response.json();
        if (isMounted) {
          setAnimationData(data);
        }
      } catch (error) {
        // Keep emoji fallback if the animation file fails to load.
      }
    };

    loadAnimation();

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <div className="w-full min-h-[calc(100vh-4rem)] overflow-x-hidden bg-gradient-to-br from-slate-100 via-indigo-50 to-cyan-100 px-4 py-6 sm:px-5 sm:py-8 flex items-center justify-center">
      <div className="max-w-2xl mx-auto w-full min-h-0 flex justify-center px-1 pb-2">
        <div className="w-full rounded-3xl bg-white border border-slate-200/80 pt-4 pb-6 px-5 sm:pt-5 sm:pb-7 sm:px-7 text-center shadow-[0_12px_40px_-8px_rgba(15,23,42,0.12),0_4px_16px_-4px_rgba(15,23,42,0.08)] ring-1 ring-slate-900/[0.04] [transform:translateZ(0)]">
          <div className="mx-auto mb-4 flex h-24 w-24 items-center justify-center [&_svg]:bg-transparent">
            {animationData ? (
              <Lottie
                animationData={animationData}
                loop={false}
                className="h-24 w-24"
                style={{ backgroundColor: 'transparent' }}
              />
            ) : (
              <span className="text-4xl leading-none">✅</span>
            )}
          </div>
          <p className="text-xs font-bold tracking-[0.22em] text-indigo-600 uppercase mb-2">
            Submission Complete
          </p>
          <h1
            className="text-3xl sm:text-4xl font-black text-slate-900 mb-3"
            style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
          >
            Your Payment Is Under Verification
          </h1>
          <p className="text-sm sm:text-base text-slate-600 leading-relaxed mb-4">
            Thank you for submitting your payment proof for <strong>{planTitle}</strong>. Our
            admin team will verify your transaction details within 24 hours and update your account
            status.
            {notifyEmail ? (
              <>
                {' '}
                We will email you at <strong className="text-slate-800">{notifyEmail}</strong> when
                your payment is approved or if we need any changes.
              </>
            ) : (
              <> We will email you when your payment is approved or if we need any changes.</>
            )}
          </p>

          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900 text-sm mb-5 leading-relaxed">
            ⏳ Verification may take up to 24 hours.
            {notifyEmail ? (
              <>
                {' '}
                Watch your inbox at <strong>{notifyEmail}</strong> for the approval update.
              </>
            ) : (
              <> Check your registered email for the approval update.</>
            )}
          </div>

          <Link
            to="/dashboard"
            className={`inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-extrabold text-white bg-gradient-to-r ${plan.accent} shadow-lg ${plan.shadow} no-underline transition-all active:scale-[0.99]`}
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PaymentVerificationStatus;
